/**
 * What each realtime event makes stale in the webapp's query cache — the ONE
 * home for "when an order changes, refresh these queries".
 *
 * Two callers read it:
 * - a mutation's `onSuccess`, through `useRefresh()`:
 *   `refresh("order.changed", { orderId })` — the same event the platform route
 *   announces for that write, so the writer and every other tab refresh alike;
 * - the realtime providers, when the event arrives from another tab, user or job.
 *
 * Keys come only from the generated `queryKeys` (never hand-written arrays), and
 * a key with fewer params is a prefix that matches every query of that route.
 * Invalidating a query nobody has mounted only marks it stale, so a set may
 * name every screen that shows the record; only mounted queries refetch.
 *
 * The map is total over `RealtimeEventName`: a new event in
 * `@birdwatch/common` fails to compile here until it says what it refreshes.
 */

import { queryKeys } from "@birdwatch/client";
import type { RealtimeEventData, RealtimeEventName } from "@birdwatch/common";
import type { QueryKey } from "@tanstack/react-query";

type RefreshKeysMap = {
  [E in RealtimeEventName]: (data: RealtimeEventData<E>) => QueryKey[];
};

/** Every query that renders one order (detail, member view, side panels that read the order row). */
const orderKeys = (orderId: string): QueryKey[] => [
  queryKeys.getOrdersByOrderId({ orderId }),
  queryKeys.getMembersOrdersByOrderId({ orderId }),
  queryKeys.getOrdersPublicByOrderId({ orderId }),
  queryKeys.getOrdersActivitiesByOrderId({ orderId }),
  queryKeys.getOrdersAttachmentsByOrderId({ orderId }),
  queryKeys.getOrdersLinkedOrdersByOrderId({ orderId }),
  queryKeys.getOrdersTimeInStatusByOrderId({ orderId }),
  queryKeys.getReportOrderOverviewHistoryByOrderId({ orderId }),
];

/** Every list or count that shows orders. */
const orderListKeys = (): QueryKey[] => [
  queryKeys.getOrdersList(),
  queryKeys.getOrdersOrderMonthList(),
  queryKeys.getOrdersStatuses(),
  queryKeys.getOrdersFeedbackList(),
  queryKeys.getReportOrderOverviewList(),
  queryKeys.getReportOrderOverviewSummary(),
  queryKeys.getMembersPlaces(),
  queryKeys.getMembersPlacesByPlaceId(),
];

/** An order's money panels. */
const orderFinanceKeys = (orderId: string | null): QueryKey[] =>
  orderId
    ? [
        queryKeys.getOrdersProfitByOrderId({ orderId }),
        queryKeys.getOrdersExpensesByOrderId({ orderId }),
        queryKeys.getOrdersRevenuesByOrderId({ orderId }),
      ]
    : [];

/** Everything that renders one home. */
const placeKeys = (placeId: string | null): QueryKey[] => [
  ...(placeId
    ? [
        queryKeys.getPlacesByPlaceId({ placeId }),
        queryKeys.getPlacesCheckByPlaceId({ placeId }),
        queryKeys.getMembersPlacesByPlaceId({ placeId }),
        // The grouped Files tab's lanes.
        queryKeys.getPlacesAttachmentsByPlaceId({ placeId }),
        // The Digital Nest tab: compiled from the home's evaluation, files and orders.
        queryKeys.getPlacesNestByPlaceId({ placeId }),
        // The rooms, systems and items a to-do can be linked to, by name.
        queryKeys.getPlacesNestPartsByPlaceId({ placeId }),
      ]
    : [
        queryKeys.getPlacesByPlaceId(),
        queryKeys.getMembersPlacesByPlaceId(),
        queryKeys.getPlacesAttachmentsByPlaceId(),
        queryKeys.getPlacesNestByPlaceId(),
        queryKeys.getPlacesNestPartsByPlaceId(),
      ]),
  // The home's HomeCare plan tab (order plans belong to a home).
  queryKeys.getPlacesOrderPlansKeysByPlaceIdByOrderPlanKey(
    placeId ? { placeId } : undefined,
  ),
  queryKeys.getPlacesList(),
  queryKeys.getMembersPlaces(),
  queryKeys.getUsersPlacesByUserId(),
  queryKeys.getAccountsPlacesByAccountId(),
];

/** Every conversation surface: threads, unread markers, the inboxes. */
const conversationKeys = (): QueryKey[] => [
  queryKeys.getChannelsMessagesByCid(),
  queryKeys.getChannelsByCid(),
  queryKeys.getChannelsUnreadByCid(),
  queryKeys.getChannelsMessagesReadsByCidByMessageId(),
  queryKeys.getChannelsInbox(),
  queryKeys.getChannelsInboxPlaces(),
  queryKeys.getChannelsInboxCounts(),
];

/** Everything on a calendar: office schedule, technician calendar, field schedule. */
const calendarKeys = (): QueryKey[] => [
  queryKeys.getSchedulesList(),
  queryKeys.getScheduleEventDetail(),
  queryKeys.getScheduleGoogleEventDetail(),
  queryKeys.getTechnicianCalendarEvents(),
  queryKeys.getFieldSchedule(),
];

export const REALTIME_REFRESH_KEYS: RefreshKeysMap = {
  "order.changed": ({ orderId }) => [
    ...orderKeys(orderId),
    ...orderFinanceKeys(orderId),
    ...orderListKeys(),
    // Inbox rows and threads show the order's title and status, and the status decides
    // whether an open thread takes messages yet (a draft or proposed order does not).
    // Every channel detail rather than this order's own: building its cid here would
    // import the messaging slice, which already imports this one.
    queryKeys.getChannelsByCid(),
    queryKeys.getChannelsInbox(),
    queryKeys.getChannelsInboxPlaces(),
    queryKeys.getChannelsInboxCounts(),
    // A home's Digital Nest lists its orders (in progress, proposed, done); the event carries no home.
    queryKeys.getPlacesNestByPlaceId(),
  ],
  "visit.changed": ({ orderId, visitId }) => [
    queryKeys.getVisitsByVisitId({ visitId }),
    queryKeys.getVisitsTechniciansByVisitId({ visitId }),
    queryKeys.getVisitsServiceTeamByVisitId({ visitId }),
    queryKeys.getVisitsAttachmentsByVisitId({ visitId }),
    queryKeys.getVisitsOfficeReportByVisitId({ visitId }),
    queryKeys.getVisitsFieldReportByVisitId({ visitId }),
    queryKeys.getVisitsOfficeExpenseByVisitId({ visitId }),
    queryKeys.getVisitsInstructionOriginByVisitId({ visitId }),
    queryKeys.getFieldVisitDetail({ visitId }),
    // Vendors › Visit monitor: texts, link openings and confirmations (a
    // technician confirming on the visit link lands here live).
    queryKeys.getVisitMonitorList(),
    queryKeys.getVisitsAll(),
    queryKeys.getVisitsCount(),
    queryKeys.getVisitsAvailableTimes(),
    queryKeys.getTimePreferencesByTimePreferenceId(),
    queryKeys.getTimeProposalsByTimeProposalId(),
    queryKeys.getOrdersLastedTimePreferencesByOrderId({ orderId }),
    // The visit's scheduling cards live in the order's conversation.
    queryKeys.getChannelsMessagesByCid(),
    ...orderKeys(orderId),
    queryKeys.getOrdersList(),
    ...calendarKeys(),
  ],
  "quote.changed": ({ orderId, quoteId }) => [
    queryKeys.getOrderQuotesList(),
    queryKeys.getOrderQuotesCounts(),
    queryKeys.getOrderQuotesByQuoteId({ quoteId }),
    queryKeys.getOrderQuotesLegacyScopePreviewByOrderId({ orderId }),
    queryKeys.getMembersQuotesByQuoteId({ quoteId }),
    queryKeys.getOrdersScopeByOrderId({ orderId }),
    queryKeys.getScopesByOrderScopeId(),
    ...orderKeys(orderId),
  ],
  "subscribers.changed": ({ orderId }) => [
    queryKeys.getOrdersByOrderId({ orderId }),
    queryKeys.getMembersOrdersByOrderId({ orderId }),
    // Every subscriber write logs a SUBSCRIBE / UNSUBSCRIBE row in the order's activity feed.
    queryKeys.getOrdersActivitiesByOrderId({ orderId }),
  ],
  "scope.changed": ({ orderId }) => [
    queryKeys.getOrdersScopeByOrderId({ orderId }),
    queryKeys.getScopesByOrderScopeId(),
    queryKeys.getLineItemsByLineItemId(),
    queryKeys.getOrderQuotesLegacyScopePreviewByOrderId({ orderId }),
    queryKeys.getHomecarePlanOrdersProposalByOrderId({ orderId }),
    ...orderFinanceKeys(orderId),
    ...orderKeys(orderId),
  ],
  "comment.changed": ({ orderId, commentId }) => [
    queryKeys.getCommentsByCommentId({ commentId }),
    queryKeys.getOrdersActivitiesByOrderId({ orderId }),
    queryKeys.getChannelsMessagesByCid(),
  ],
  "message.changed": ({ messageId }) => [
    ...conversationKeys(),
    ...(messageId
      ? [
          queryKeys.getMessagesByMessageId({ messageId }),
          // A post can carry a scheduling card, and a card's content is a SECOND read
          // keyed by the id in its block payload (`useChannelTimePreference`): the
          // homeowner's shared availability attaches its slots to the very row the
          // "Availability requested" block already cached, and the platform announces
          // that write as `message.changed` alone (it writes `TimePreferenceItem` rows
          // and no visit row, so no `visit.changed`). Without this the message lands in
          // the refetched list and draws nothing — the cached row still has no items,
          // and the block renders null for an empty slot list. The posting tab already
          // does this for itself in `useSendOrderMessage`.
          queryKeys.getTimePreferencesByTimePreferenceId(),
          // The office's "Latest time preferences" card reads the same answer off the
          // order. It is held at `staleTime: Infinity` (`use-latest-time-preferences.ts`),
          // so invalidation is the ONLY thing that ever updates it — without this it
          // keeps offering to await a reply the homeowner has already sent. Prefix: the
          // event names the opaque `Channel.id`, never an order, and only the order the
          // office has open is mounted.
          queryKeys.getOrdersLastedTimePreferencesByOrderId(),
        ]
      : []),
    // A post can ask the homeowner something, and an answer settles a prompt.
    queryKeys.getChannelsPromptBlocksByCid(),
  ],
  // A conversation was archived or unarchived (ENG-4122): it moves between the active
  // and Archived lists, and an open thread gains or loses its composer. Every channel
  // detail rather than this one's: the event names the opaque `Channel.id`, and the
  // detail query is keyed by cid.
  "channel.changed": () => [
    queryKeys.getChannelsByCid(),
    queryKeys.getChannelsInbox(),
    queryKeys.getChannelsInboxPlaces(),
    queryKeys.getChannelsInboxCounts(),
  ],
  // Typing is rendered from the event itself (channel-typing), not refetched.
  "typing.changed": () => [],
  "orderInsight.changed": ({ orderId, kind }) => {
    switch (kind) {
      case "SUMMARY":
        return [queryKeys.getOrdersAiSummaryByOrderId({ orderId })];
      case "BIRDIE_TIPS":
        return [queryKeys.getOrdersBirdieTipsByOrderId({ orderId })];
      case "TONE":
        return [queryKeys.getOrdersToneByOrderId({ orderId })];
      case "CONVERSATION_SUMMARY":
        return [queryKeys.getOrdersConversationSummaryByOrderId({ orderId })];
      case "VISIT_INSTRUCTION":
        return [
          queryKeys.getOrdersByOrderId({ orderId }),
          queryKeys.getVisitsByVisitId(),
        ];
      case "RECAP":
        return [
          queryKeys.getReportOrderOverviewHistoryByOrderId({ orderId }),
          queryKeys.getReportOrderOverviewList(),
        ];
      default:
        // A kind newer than this build (a tab left open across a deploy): its panel is not
        // rendered here, so nothing is stale.
        return [];
    }
  },
  "expense.changed": ({ expenseId, orderId }) => [
    queryKeys.getExpensesList(),
    queryKeys.getExpensesByExpenseId({ expenseId }),
    queryKeys.getVisitsOfficeExpenseByVisitId(),
    ...orderFinanceKeys(orderId),
    // A visit's detail and its office/field reports embed the report's expenses.
    queryKeys.getVisitsByVisitId(),
    queryKeys.getVisitsOfficeReportByVisitId(),
    queryKeys.getVisitsFieldReportByVisitId(),
  ],
  "timeEntry.changed": ({ orderId }) => [
    queryKeys.getTimeEntriesList(),
    ...orderFinanceKeys(orderId),
    ...(orderId ? [queryKeys.getOrdersByOrderId({ orderId })] : []),
  ],
  "revenue.changed": ({ orderId }) => [...orderFinanceKeys(orderId)],
  "homecareProposal.changed": ({ proposalId, orderId }) => [
    queryKeys.getHomecarePlanProposals(),
    queryKeys.getHomecarePlanProposalsByProposalId({ proposalId }),
    queryKeys.getHomecarePlanProposalsDetailByProposalId({ proposalId }),
    ...(orderId
      ? [queryKeys.getHomecarePlanOrdersProposalByOrderId({ orderId })]
      : []),
  ],
  // The boards (team channel) and the order card (the order's channel). A
  // change moves the board's rows, tiles and tab counts, the forecast, the
  // open popup, and the order's "why there is (no) Smart Action" line.
  "smartAction.changed": ({ smartActionId, orderId }) => [
    queryKeys.getSmartActionsList(),
    queryKeys.getSmartActionsSummary(),
    queryKeys.getSmartActionsForecast(),
    queryKeys.getSmartActionsBySmartActionId({ smartActionId }),
    queryKeys.getSmartActionsOrderReviewByOrderId({ orderId }),
    // The AI feedback capture route announces smartAction.changed; Reports → AI
    // feedback lists the feedback.
    queryKeys.getAiFeedbackList(),
  ],
  "task.changed": ({ taskId, orderId }) => [
    queryKeys.getTasksList(),
    queryKeys.getTasksGroups(),
    queryKeys.getTasksByTaskId({ taskId }),
    queryKeys.getGlobalOffice(),
    ...(orderId ? [queryKeys.getOrdersActivitiesByOrderId({ orderId })] : []),
    // A task's uploads are attached to its order too.
    ...(orderId ? [queryKeys.getOrdersAttachmentsByOrderId({ orderId })] : []),
    // A Smart Action step shows the task it links (subject, assignee, due, done).
    // Prefix: the event names no action, and a step may link a related order's task.
    queryKeys.getSmartActionsBySmartActionId(),
  ],
  "orderRequest.changed": ({ orderRequestId }) => [
    queryKeys.getOrderRequestsList(),
    queryKeys.getOrderRequestsGroupCounts(),
    queryKeys.getOrderRequestsStatuses(),
    queryKeys.getOrderRequestsByOrderRequestId({ orderRequestId }),
    queryKeys.getOrderRequestsActivitiesByOrderRequestId({ orderRequestId }),
    queryKeys.getOrderRequestsTimeInStatusByOrderRequestId({ orderRequestId }),
    queryKeys.getOrderRequestsSubscribersByOrderRequestId({ orderRequestId }),
    queryKeys.getOrderRequestsItemsSuggestByOrderRequestId({ orderRequestId }),
    // A cart line moves its source list item's `context` (in a draft, requested).
    queryKeys.getListItemsList(),
    queryKeys.getListItemsByListItemId(),
  ],
  "place.changed": ({ placeId }) => [
    ...placeKeys(placeId),
    // A list item's detail names its rooms as the Nest does (`includes=nestLinks`): a renamed room shows there.
    queryKeys.getListItemsByListItemId(),
  ],
  "household.changed": ({ placeId }) => [
    queryKeys.getUsersInvitesPlacesByPlaceId({ placeId }),
    queryKeys.getUsersInvitesPlacesMembersByPlaceId({ placeId }),
    queryKeys.getAccountsUsersByAccountId(),
    ...placeKeys(placeId),
    // The Office Inbox's "Your members" lists the homes a user coordinates — a
    // `UserPlace` row — so a change to who belongs to a home moves its conversations.
    queryKeys.getChannelsInbox(),
    queryKeys.getChannelsInboxPlaces(),
    queryKeys.getChannelsInboxCounts(),
  ],
  "space.changed": ({ placeId, spaceId }) => [
    ...(spaceId ? [queryKeys.getSpacesBySpaceId({ spaceId })] : []),
    queryKeys.getGlobalSpaces(),
    queryKeys.getListItemsList(),
    queryKeys.getPlacesByPlaceId({ placeId }),
    queryKeys.getMembersPlacesByPlaceId({ placeId }),
    // A list item's detail lists the spaces it is filed under, by name.
    queryKeys.getListItemsByListItemId(),
  ],
  "listItem.changed": ({ placeId, listItemId }) => [
    queryKeys.getListItemsList(),
    ...(listItemId
      ? [queryKeys.getListItemsByListItemId({ listItemId })]
      : [queryKeys.getListItemsByListItemId()]),
    ...(placeId
      ? [
          queryKeys.getPlacesByPlaceId({ placeId }),
          queryKeys.getMembersPlacesByPlaceId({ placeId }),
          // The Digital Nest lists the home's to-dos on each part (`listItems`).
          queryKeys.getPlacesNestByPlaceId({ placeId }),
        ]
      : []),
    // A space's detail lists the items linked to it.
    queryKeys.getSpacesBySpaceId(),
  ],
  "note.changed": ({ notableType, notableId }) => [
    queryKeys.getNotesDocByNotableTypeByNotableId({ notableType, notableId }),
  ],
  "onboarding.changed": ({ onboardingId, placeId }) => [
    queryKeys.getOnboardingsList(),
    queryKeys.getOnboardingsStages(),
    ...(onboardingId
      ? [
          queryKeys.getOnboardingsByOnboardingId({ onboardingId }),
          queryKeys.getOnboardingsWidgetByOnboardingId({ onboardingId }),
        ]
      : [
          queryKeys.getOnboardingsByOnboardingId(),
          queryKeys.getOnboardingsWidgetByOnboardingId(),
        ]),
    queryKeys.getAnalyticsMemberActivationRoster(),
    queryKeys.getAnalyticsMemberActivationSummary(),
    // The home's detail carries its onboarding (`latestMembership.onboardingId`,
    // which a reset replaces, and `onboardings[]`).
    ...(placeId ? [queryKeys.getPlacesByPlaceId({ placeId })] : []),
    // The Office Inbox's Onboarding inbox lists the homes whose onboarding is in
    // progress, so one starting or completing moves its home's conversations.
    queryKeys.getChannelsInbox(),
    queryKeys.getChannelsInboxPlaces(),
    queryKeys.getChannelsInboxCounts(),
  ],
  "account.changed": ({ accountId }) => [
    queryKeys.getAccountsByAccountId({ accountId }),
    queryKeys.getAccountsUsersByAccountId({ accountId }),
    queryKeys.getAccountsPlacesByAccountId({ accountId }),
    queryKeys.getAccountsList(),
    // A vendor is keyed by its account: its detail renders the account's fields.
    queryKeys.getVendorsByVendorId({ vendorId: accountId }),
    // The vendors list renders account fields too (name, contact, status, primary contact).
    queryKeys.getVendorsList(),
  ],
  "billing.changed": ({ accountId }) => [
    queryKeys.getAccountsPaymentMethodsByAccountId({ accountId }),
    queryKeys.getBillingPaymentMethodsByAccountId({ accountId }),
    queryKeys.getBillingPaymentMethodsByPaymentMethodId(),
    queryKeys.getBillingPaymentHistoriesByAccountId({ accountId }),
    queryKeys.getBillingInvoicesByAccountId({ accountId }),
    // The member's billing alert reads overdue invoices and the default method.
    queryKeys.getMembersBillingAlert(),
  ],
  "subscription.changed": ({ subscriptionId, accountId }) => [
    // A subscription paused or resumed moves the member's billing alert.
    queryKeys.getMembersBillingAlert(),
    queryKeys.getSubscriptionsList(),
    queryKeys.getSubscriptionsStatuses(),
    queryKeys.getSubscriptionsBySubscriptionId({ subscriptionId }),
    queryKeys.getSubscriptionsMyBySubscriptionId({ subscriptionId }),
    ...(accountId ? [queryKeys.getAccountsByAccountId({ accountId })] : []),
    queryKeys.getAuthMe(),
    // The account's homes list and the customers list show each home's plan and its checkout-required flag.
    ...(accountId
      ? [queryKeys.getAccountsPlacesByAccountId({ accountId })]
      : [queryKeys.getAccountsPlacesByAccountId()]),
    queryKeys.getAccountsList(),
  ],
  "membership.changed": ({ accountId }) => [
    // A paused membership is the account-paused billing alert.
    queryKeys.getMembersBillingAlert(),
    ...(accountId ? [queryKeys.getAccountsByAccountId({ accountId })] : []),
    queryKeys.getAccountsList(),
    queryKeys.getAuthMe(),
    // A member's activation is counted on the activation dashboard.
    queryKeys.getAnalyticsMemberActivationRoster(),
    queryKeys.getAnalyticsMemberActivationSummary(),
    // A subscription's detail and the subscriptions list embed its memberships.
    queryKeys.getSubscriptionsBySubscriptionId(),
    queryKeys.getSubscriptionsList(),
    // The account's homes list shows each home's plan and its checkout-required flag.
    ...(accountId
      ? [queryKeys.getAccountsPlacesByAccountId({ accountId })]
      : [queryKeys.getAccountsPlacesByAccountId()]),
  ],
  "wallet.changed": ({ accountId }) => [
    queryKeys.getWalletsByAccountId({ accountId }),
    queryKeys.getWalletsTransactionHistoriesByAccountId({ accountId }),
    queryKeys.getWalletsTransactionHistoriesByLedgerId(),
  ],
  "purchaseIntent.changed": ({ purchaseIntentId }) => [
    queryKeys.getPurchaseIntentsList(),
    queryKeys.getPurchaseIntentsByPurchaseIntentId({ purchaseIntentId }),
    queryKeys.getCheckoutPurchaseIntents(),
    queryKeys.getCheckoutPurchaseIntentsByPurchaseIntentId({
      purchaseIntentId,
    }),
    // The account's homes list and the customers list show each home's checkout-required
    // flag; the event names no account, so these refresh by prefix.
    queryKeys.getAccountsPlacesByAccountId(),
    queryKeys.getAccountsList(),
  ],
  "checkoutLink.changed": ({ checkoutLinkId }) => [
    queryKeys.getCheckoutLinksList(),
    queryKeys.getCheckoutLinksByCheckoutLinkId({ checkoutLinkId }),
    queryKeys.getCheckoutLinksPlanByCheckoutLinkId({ checkoutLinkId }),
  ],
  "user.changed": ({ userId }) => [
    queryKeys.getUsersByUserId({ userId }),
    queryKeys.getUsersProfilesByUserId({ userId }),
    queryKeys.getUsersAccountsByUserId({ userId }),
    queryKeys.getUsersPlacesByUserId({ userId }),
    queryKeys.getPolicyUsersByUserId({ userId }),
    queryKeys.getUsersList(),
    queryKeys.getAccountsUsersByAccountId(),
    queryKeys.getAuthMe(),
    queryKeys.getUsersMeNotificationPreferences(),
  ],
  // Rides the reader's own `user:` channel (UserRealtimeProvider), office and member app alike.
  "userPresence.changed": ({ userId }) => [
    queryKeys.getUsersOnlineByUserId({ userId }),
  ],
  "vendor.changed": ({ vendorId }) => [
    queryKeys.getVendorsList(),
    queryKeys.getVendorsByVendorId({ vendorId }),
  ],
  "vendorOnboarding.changed": () => [queryKeys.getVendorsList()],
  "scheduleEvent.changed": () => calendarKeys(),
  "calendar.changed": () => [...calendarKeys(), queryKeys.getAuthMe()],
  "meet.changed": () => [
    queryKeys.getMeetLinkByName(),
    queryKeys.getMeetParticipantsByRoomName(),
  ],
  "role.changed": ({ roleKey }) => [
    queryKeys.getPolicyRoles(),
    queryKeys.getPolicyRolesByRoleKey({ roleKey }),
    queryKeys.getPolicyPermissions(),
    queryKeys.getPolicyUsersByUserId(),
  ],
  "tag.changed": () => [queryKeys.getTagsList()],
  "campaign.changed": ({ campaignId }) => [
    queryKeys.getCampaignsList(),
    queryKeys.getCampaignsByCampaignId({ campaignId }),
    queryKeys.getMembersCampaigns(),
  ],
  "location.changed": ({ locationId }) => [
    queryKeys.getLocationsList(),
    queryKeys.getLocationsByLocationId({ locationId }),
  ],
  "zipcode.changed": ({ zipcode }) => [
    queryKeys.getZipcodesList(),
    queryKeys.getZipcodesByZipcode({ zipcode }),
    queryKeys.getZipcodesCities(),
  ],
  "view.changed": () => [
    queryKeys.getViewsList(),
    queryKeys.getViewsDefaults(),
  ],
  // `channelTypeId` is `order_comment` alone today (the event's enum): every answer that
  // counts the reader's unread comments, and the open notes thread a single read refreshes.
  "readState.changed": () => [
    queryKeys.getChannelsMessagesByCid(),
    queryKeys.getChannelsByCid(),
    queryKeys.getChannelsUnreadByCid(),
    queryKeys.getChannelsMessagesReadsByCidByMessageId(),
    queryKeys.getFieldConversations(),
    queryKeys.getFieldConversationCounts(),
    queryKeys.getFieldOrderConversation(),
    // `totalNewComments` on the visit screen and the calendar's visit events.
    queryKeys.getFieldVisitDetail(),
    queryKeys.getTechnicianCalendarEvents(),
  ],
  "notification.received": () => [
    queryKeys.getNotificationsList(),
    queryKeys.getNotificationsLatest(),
    queryKeys.getNotificationsCounts(),
  ],
  "notification.counts": () => [
    queryKeys.getNotificationsCounts(),
    // The office bell badge is a `countOnly` list query, and a moved badge means
    // rows changed read/archive state.
    queryKeys.getNotificationsList(),
  ],
  "inbox.activity": () => [
    queryKeys.getChannelsInbox(),
    queryKeys.getChannelsInboxPlaces(),
    queryKeys.getChannelsInboxCounts(),
    // A thread's new message moves the orders lists' unread `newMessagesForOwner` include.
    queryKeys.getOrdersList(),
    // The Digital Nest shows an open order's next visit.
    queryKeys.getPlacesNestByPlaceId(),
  ],
  "attachment.processed": ({ attachmentId }) => [
    queryKeys.getAttachmentsByAttachmentId({ attachmentId }),
  ],
  // The transcript is delivered in the event itself (media-upload, voice capture).
  "transcript.ready": () => [],
  "transcript.failed": () => [],
  // A release offers a reload (app-version); nothing to refetch.
  "app.released": () => [],
};

/** The queries one event makes stale. */
export function refreshKeysFor<E extends RealtimeEventName>(
  event: E,
  data: RealtimeEventData<E>,
): QueryKey[] {
  return REALTIME_REFRESH_KEYS[event](data);
}

/**
 * What adding files to an order or removing them makes stale, for the writer's
 * `onSuccess` — a gallery upload or delete, or a message or comment that
 * carries files into the order's threads.
 *
 * The platform announces two events for it: `order.changed` (the order's
 * Files tab) and `place.changed` on the order's home, whose Files tab lists
 * every order's files. The writer holds only the order, so the home's detail
 * and files refresh by prefix — only mounted queries refetch, and the event's
 * echo covers the rest of the home's set.
 *
 * A grouped Files tab lists its uploaders and visits from the users and visits
 * lists, and a first upload can add a lane to either. They refresh here, for
 * the writer only, rather than on every order event any screen hears.
 */
export function orderAttachmentsRefreshKeys(orderId: string): QueryKey[] {
  return [
    ...REALTIME_REFRESH_KEYS["order.changed"]({ orderId }),
    queryKeys.getPlacesByPlaceId(),
    queryKeys.getPlacesAttachmentsByPlaceId(),
    queryKeys.getPlacesNestByPlaceId(),
    queryKeys.getUsersList(),
    queryKeys.getVisitsAll(),
  ];
}

/** Every event name, for subscribing to all of them. */
export const REALTIME_REFRESH_EVENTS = Object.keys(
  REALTIME_REFRESH_KEYS,
) as RealtimeEventName[];
